import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { Link } from 'react-router-dom';

import { ICONS, PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';

import Message from 'components/Message';

import messages from './WeeklyEarnings.messages';
import './WeeklyEarningsTooltip.scss';

const { block, element } = bem('weekly-earnings-tooltip');

export const CTA_DESTINATION = Object.freeze({
  CAMPAIGN: 'campaign',
  OPPORTUNITIES: 'opportunities',
});

export const TOOLTIP_VARIANT = Object.freeze({
  EARNINGS: 'earnings',
  NO_SPOTS: 'no_spots',
  NO_CP_BOOKINGS: 'no_cp_bookings',
});

export const tooltipShape = PropTypes.shape({
  title: PropTypes.string.isRequired,
  periodSubtitle: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(TOOLTIP_VARIANT)),
  stats: PropTypes.arrayOf(PropTypes.shape({})),
  ctaDestination: PropTypes.string,
  calloutMessage: PropTypes.shape({ id: PropTypes.string.isRequired }),
});

export const resolveCallout = (excessSpotsFilledPercent, classpassBooked) => {
  const pct = Number(excessSpotsFilledPercent) || 0;
  const bookings = Number(classpassBooked) || 0;

  if (pct >= 31) {
    if (bookings >= 16) {
      return { message: messages.tooltipCalloutExcess31PlusRes16Plus, destination: CTA_DESTINATION.OPPORTUNITIES };
    }
    if (bookings >= 4) {
      return { message: messages.tooltipCalloutExcess31PlusRes4to15, destination: CTA_DESTINATION.OPPORTUNITIES };
    }
    return { message: messages.tooltipCalloutExcess31PlusRes0to3, destination: CTA_DESTINATION.CAMPAIGN };
  }
  if (pct >= 11) {
    if (bookings >= 16) {
      return { message: messages.tooltipCalloutExcess11to30Res16Plus, destination: CTA_DESTINATION.OPPORTUNITIES };
    }
    if (bookings >= 4) {
      return { message: messages.tooltipCalloutExcess11to30Res4to15, destination: CTA_DESTINATION.CAMPAIGN };
    }
    return { message: messages.tooltipCalloutExcess11to30Res0to3, destination: CTA_DESTINATION.CAMPAIGN };
  }
  if (bookings >= 16) {
    return { message: messages.tooltipCalloutExcess0to10Res16Plus, destination: CTA_DESTINATION.CAMPAIGN };
  }
  if (bookings >= 4) {
    return { message: messages.tooltipCalloutExcess0to10Res4to15, destination: CTA_DESTINATION.CAMPAIGN };
  }
  return { message: messages.tooltipCalloutExcess0to10Res0to3, destination: CTA_DESTINATION.CAMPAIGN };
};

export const resolveCta = (excessSpotsFilledPercent, classpassBooked) =>
  resolveCallout(excessSpotsFilledPercent, classpassBooked).destination;

function TooltipCallout({ calloutMessage, values, icon = ICONS.marketingGraph }) {
  return (
    <div className={element('callout')}>
      <Isvg aria-hidden className={element('callout-icon')} src={icon} />
      <p className={element('callout-text')}>
        <Message message={calloutMessage} values={values} />
      </p>
    </div>
  );
}
TooltipCallout.propTypes = {
  calloutMessage: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  icon: PropTypes.string,
  values: PropTypes.shape({}),
};

function WeeklyEarningsTooltip({ tooltip, campaignsPath, opportunitiesPath, hideTitle, venueId }) {
  const { title, stats, ctaDestination, calloutMessage, variant } = tooltip;

  const trackCta = type => {
    Segment.event('Click SmartRate Hover CTA', { type, venue_id: venueId });
  };

  const campaignLink = text => (
    <Link className={element('link')} onClick={() => trackCta('Campaigns')} to={campaignsPath}>
      {text}
    </Link>
  );

  const opportunitiesLink = text => (
    <Link className={element('link')} onClick={() => trackCta('Opportunities')} to={opportunitiesPath}>
      {text}
    </Link>
  );

  const renderBody = () => {
    if (variant === TOOLTIP_VARIANT.NO_SPOTS) {
      return (
        <p className={element('body')}>
          <Message message={messages.tooltipNoSpotsBody} />
        </p>
      );
    }
    if (variant === TOOLTIP_VARIANT.NO_CP_BOOKINGS) {
      return (
        <p className={element('body')}>
          <Message message={messages.tooltipNoCpBookingsBody} />
        </p>
      );
    }
    return null;
  };

  const renderCallout = () => {
    if (variant === TOOLTIP_VARIANT.NO_SPOTS) {
      return (
        <TooltipCallout
          calloutMessage={messages.tooltipNoSpotsCallout}
          icon={PFE_ICONS.smartSpotShield}
          values={{ a: opportunitiesLink }}
        />
      );
    }

    if (variant === TOOLTIP_VARIANT.NO_CP_BOOKINGS) {
      return <TooltipCallout calloutMessage={messages.tooltipNoCpBookingsCallout} values={{ a: campaignLink }} />;
    }

    if (!calloutMessage) {
      return null;
    }

    const link = ctaDestination === CTA_DESTINATION.OPPORTUNITIES ? opportunitiesLink : campaignLink;
    return <TooltipCallout calloutMessage={calloutMessage} values={{ a: link }} />;
  };

  return (
    <div className={block()}>
      {!hideTitle && <p className={element('title')}>{title}</p>}
      {renderBody()}
      {stats && stats.length > 0 && (
        <div className={element('stats')}>
          {stats.map(({ key, label, value }) => (
            <div className={element('stat')} key={key}>
              <span className={element('stat-label')}>
                <Message message={label.message} values={label.values} />
              </span>
              <span className={element('stat-value')}>{value}</span>
            </div>
          ))}
        </div>
      )}
      {renderCallout()}
    </div>
  );
}

WeeklyEarningsTooltip.propTypes = {
  hideTitle: PropTypes.bool,
  venueId: PropTypes.number,
  tooltip: PropTypes.shape({
    title: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(Object.values(TOOLTIP_VARIANT)),
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.shape({
          message: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
          values: PropTypes.shape({}),
        }).isRequired,
        value: PropTypes.node.isRequired,
      })
    ),
    ctaDestination: PropTypes.oneOf(Object.values(CTA_DESTINATION)),
    calloutMessage: PropTypes.shape({ id: PropTypes.string.isRequired }),
  }).isRequired,
  campaignsPath: PropTypes.string.isRequired,
  opportunitiesPath: PropTypes.string.isRequired,
};

export default WeeklyEarningsTooltip;
